import { useMemo } from 'react';
import { validate as isUuid } from 'uuid';
import { useIdentity } from '$api-clients/hooks';

/**
 * Resolves a potentially legacy ID to an identity UUID.
 * If the ID is already a UUID, returns it directly without making an API call.
 * If it's a legacy ID, calls the identity service to resolve it.
 */
export function useResolveUserIdentityId(userId: string | null | undefined) {
  const isLegacyId = !!userId && !isUuid(userId);

  const { data, isLoading } = useIdentity(
    '/users/{userId}',
    isLegacyId ? { params: { path: { userId } } } : null,
  );

  if (!userId) return { resolvedId: null, isLoading: false };
  if (!isLegacyId) return { resolvedId: userId, isLoading: false };

  return { resolvedId: data?.id ?? null, isLoading };
}

/**
 * Resolves a mix of legacy and identity user IDs to identity UUIDs in one
 * request, for callers that can't loop {@link useResolveUserIdentityId}.
 * Identity's `ids` filter matches legacy IDs too, so only those need sending.
 * Unresolvable IDs are absent from the returned set. `skip` suppresses the
 * request for callers that already know the answer without it.
 */
export function useResolveUserIdentityIds(
  userIds: (string | null | undefined)[],
  { skip = false }: { skip?: boolean } = {},
) {
  // Callers build this list inline, so its array identity changes every render.
  // Keying on the contents is what keeps `resolvedIds` referentially stable.
  const idsKey = [...new Set(userIds.filter((id) => !!id))].sort().join(',');

  const { identityIds, legacyIds } = useMemo(() => {
    const ids = idsKey ? idsKey.split(',') : [];
    return {
      identityIds: ids.filter((id) => isUuid(id)),
      legacyIds: ids.filter((id) => !isUuid(id)),
    };
  }, [idsKey]);

  const { data, isLoading } = useIdentity(
    '/users',
    legacyIds.length && !skip
      ? {
          // Without an explicit limit the response is capped at the service
          // default, silently dropping resolutions for a long id list.
          params: {
            query: { ids: legacyIds.join(','), limit: legacyIds.length },
          },
        }
      : null,
  );

  const resolvedIds = useMemo(
    () =>
      new Set([...identityIds, ...(data?.data ?? []).map((user) => user.id)]),
    [identityIds, data],
  );

  return { resolvedIds, isLoading };
}

/**
 * Resolves a potentially legacy team ID to an identity UUID.
 * If the ID is already a UUID, returns it directly without making an API call.
 * If it's a legacy ID, calls the identity service to resolve it.
 */
export function useResolveTeamIdentityId(teamId: string | null | undefined) {
  const isLegacyId = !!teamId && !isUuid(teamId);

  const { data, isLoading } = useIdentity(
    '/teams/{teamId}',
    isLegacyId ? { params: { path: { teamId } } } : null,
  );

  if (!teamId) return { resolvedId: null, isLoading: false };
  if (!isLegacyId) return { resolvedId: teamId, isLoading: false };

  return { resolvedId: data?.id ?? null, isLoading };
}
